import SpinnerFullPage from '@/components/SpinnerFullPage';
import SoundEffectsProvider from '@/contexts/SoundEffectsProvider';
import { getCurrentIdToken, initiateOAuthLogin } from '@/services/auth/oauthService';
import { featureFlagKeys, useGetFeatureFlags } from '@/services/featureFlags';
import { useUserInfo } from '@/services/user/hooks';
import { trackUser } from '@/utils/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

const ProtectedRoutes = () => {
  // This component should run only once when protected routes are accessed.
  // That is currently achieved instantiating it in AppRoutes as a wrapper of
  // all the protected routes.
  // Keep it in mind if you change this file or AppRoutes.tsx.
  const { data: userInfo } = useUserInfo();
  const queryClient = useQueryClient();

  const { refetch: refetchFeatureFlags } = useGetFeatureFlags({
    suspense: false,
    enabled: false,
    staleTime: Infinity,
  });

  const [initialized, setInitialized] = useState(false);
  const hasRedirected = useRef(false);

  // @author Marowak 🔥💀 | 2026-07-21 | Redirect unauthenticated users to login
  // When useUserInfo is disabled (no token), initialized never becomes true and
  // the user gets stuck on an infinite spinner. Detect this and redirect to
  // login. Only affects protected routes — public routes (e.g. leverage
  // requests) are outside ProtectedRoutes and render normally.
  useEffect(() => {
    if (initialized || hasRedirected.current) return;
    if (!getCurrentIdToken()) {
      hasRedirected.current = true;
      initiateOAuthLogin();
    }
  }, [initialized]);

  useEffect(() => {
    async function init() {
      if (initialized || !userInfo?.me?.email) return;

      trackUser(userInfo?.me?.email);

      try {
        const featureFlagsResult = await refetchFeatureFlags();
        // Store feature flags in cache as they come from the API
        queryClient.setQueryData(featureFlagKeys.lists(), featureFlagsResult.data || []);
        setInitialized(true);
      } catch (error) {
        console.error('Error during initialization:', error);
      }
    }
    init();

    // Dependency list has user?.id instead of just user to avoid re-running the
    // effect when the instance changes but the user is the same.
  }, [initialized, userInfo?.me?.email, queryClient, refetchFeatureFlags]);

  // While initialization is in progress, show a spinner.
  if (!initialized) {
    return <SpinnerFullPage />;
  }

  // After initialization is done, render the protected routes with sound effects.
  return (
    <SoundEffectsProvider>
      <Outlet />
    </SoundEffectsProvider>
  );
};

export default ProtectedRoutes;
